// Privacy masking — #217.
//
// Two responsibilities, both client-side, both BEFORE upload:
//   1. Visual masking — when the widget captures a screenshot, elements
//      matching the configured CSS selectors are blanked out so the
//      bytes never include their pixels. Implemented by ADOPTING a
//      constructable stylesheet onto the document (`background: solid;
//      color: transparent; outline: dashed`) just before html-to-image
//      runs, then detaching it after the capture lands. #584 moved this
//      off a <style> element, which a strict host CSP silently blocks.
//   2. String redaction — when the widget builds a submission payload,
//      every string value in the console log + network error log gets
//      regex-replaced with `[REDACTED]`. Catches CC numbers / emails /
//      SSNs that ended up in console.log() output or fetch URLs.
//
// Defaults are ALWAYS applied. Workspace-configured arrays from
// /api/v1/widget-config (privacyConfig.maskSelectors and
// .redactPatterns) ADD to the defaults; they don't replace. So even a
// new workspace with no config gets password + email + CC masking.
//
// Bundle impact: ~2 KB gzipped (regex compilation + DOM walking).

import { adoptStyles } from './csp.ts';

/**
 * Selectors masked by default in screenshots, regardless of workspace
 * config. Covers the common PII inputs every site has:
 *   - password fields
 *   - email fields
 *   - explicit opt-in via the documented data-oncatch-mask attribute
 */
export const DEFAULT_MASK_SELECTORS: ReadonlyArray<string> = [
  'input[type="password"]',
  'input[type="email"]',
  '[data-oncatch-mask]',
];

/**
 * Regex patterns redacted by default in console + network log strings.
 * String form so they're cheap to ship + serialize; compiled to RegExp
 * once at use time. Patterns are intentionally conservative — false
 * positives (over-masking) are preferred over false negatives (PII
 * leak).
 */
export const DEFAULT_REDACT_PATTERNS: ReadonlyArray<string> = [
  // Credit-card-shaped digit runs (13-19 digits, optionally separated
  // by spaces or dashes). Matches Visa / Mastercard / Amex / Discover
  // shapes without strict Luhn validation.
  String.raw`\b(?:\d[ -]*?){13,19}\b`,
  // Email addresses — matches against a permissive grammar; deliberately
  // not RFC-strict so we catch "almost-emails" too.
  String.raw`[\w.+-]+@[\w-]+\.[\w.-]+`,
  // US SSN format (NNN-NN-NNNN). Doesn't catch space-separated or
  // unformatted; customer-configured patterns can extend this.
  String.raw`\b\d{3}-\d{2}-\d{4}\b`,
];

const REDACTION_PLACEHOLDER = '[REDACTED]';

/**
 * Combine the workspace's configured extra selectors with the always-on
 * defaults. Empty / undefined extras → just the defaults. Caller passes
 * the joined string to a single document.querySelectorAll for cheap
 * matching.
 */
export function combineMaskSelectors(extras: ReadonlyArray<string> | undefined): string {
  const seen = new Set<string>();
  const all: string[] = [];
  for (const sel of [...DEFAULT_MASK_SELECTORS, ...(extras ?? [])]) {
    const trimmed = sel.trim();
    if (!trimmed || seen.has(trimmed)) continue;
    seen.add(trimmed);
    all.push(trimmed);
  }
  return all.join(', ');
}

/**
 * Compile the workspace's configured extra patterns alongside the
 * defaults. Invalid regex strings are skipped (logged for telemetry by
 * the caller, never thrown — bad config shouldn't crash a submission).
 */
export function compileRedactPatterns(extras: ReadonlyArray<string> | undefined): RegExp[] {
  const out: RegExp[] = [];
  for (const src of [...DEFAULT_REDACT_PATTERNS, ...(extras ?? [])]) {
    try {
      out.push(new RegExp(src, 'g'));
    } catch {
      // Customer-configured invalid pattern. Skip silently — submission
      // path mustn't break on bad config. A future telemetry hook could
      // surface these to admin observability.
    }
  }
  return out;
}

/**
 * Inject a <style> tag that visually blanks any matching elements
 * during screenshot capture. Returns a teardown function the caller
 * MUST run in a `finally` block — leaving the style attached would
 * permanently break the customer's page UX.
 *
 * The CSS uses `!important` because the customer's own styles may
 * specify backgrounds / colors with high specificity. Outline +
 * background combo gives a recognizable "hidden field" look in the
 * resulting screenshot rather than a totally-blank rectangle the
 * reviewer might not notice.
 */
export function applyScreenshotMask(combinedSelector: string): () => void {
  if (!combinedSelector || typeof document === 'undefined') return () => undefined;
  // #584: adopted onto the document rather than injected as a <style> element.
  // A host CSP with `style-src-elem 'self' 'nonce-…'` refuses the un-nonced
  // element, so the mask silently did nothing on strict-CSP hosts — the
  // capture ran with passwords and emails fully visible, no error anywhere.
  // The defaults in DEFAULT_MASK_SELECTORS are always on, so this affected
  // every workspace on such a host, not only ones with custom selectors.
  return adoptStyles(
    document,
    `
    ${combinedSelector} {
      background: #1a1a1a !important;
      color: transparent !important;
      outline: 1px dashed rgba(0,0,0,0.35) !important;
      outline-offset: -1px !important;
      caret-color: transparent !important;
      text-shadow: none !important;
      box-shadow: none !important;
    }
    ${combinedSelector}::placeholder {
      color: transparent !important;
    }
  `,
  );
}

/**
 * Apply each compiled pattern across every string value in the input
 * (recursively walks objects + arrays). Returns a NEW value — never
 * mutates the input. Non-string scalars pass through untouched.
 *
 * Used for console + network log redaction in the submission payload.
 */
export function redactStringValues<T>(input: T, patterns: ReadonlyArray<RegExp>): T {
  if (patterns.length === 0) return input;
  return walk(input, patterns) as T;
}

function walk(value: unknown, patterns: ReadonlyArray<RegExp>): unknown {
  if (typeof value === 'string') {
    let out = value;
    for (const pattern of patterns) {
      // Reset lastIndex defensively — global RegExp state survives across
      // .replace() calls and would mis-match on the second pass otherwise.
      pattern.lastIndex = 0;
      out = out.replace(pattern, REDACTION_PLACEHOLDER);
    }
    return out;
  }
  if (Array.isArray(value)) {
    return value.map((v) => walk(v, patterns));
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(value as Record<string, unknown>)) {
      out[key] = walk((value as Record<string, unknown>)[key], patterns);
    }
    return out;
  }
  return value;
}
